import "./envios.scss";
import { useEffect, useMemo, useState } from "react";
import { Plus, Check } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { fetchEnvios, setModalAddStatus, confirmEnvio } from "../../../store/EnviosSlice";
import { fetchClientsByOrganization } from "../../../store/UserSlice";
import { fetchAssets, fetchNetowrks } from "../../../store/AssetsSlice";
import { formatAssetAmount } from "../../../utils/decimal";
import { DataList, DataListColumn, StatusBadge, FormField } from "../../../components/ui";
import { IEnvio } from "../../../models/Envio";
import ModalAddEnvio from "../../../components/modal/modal-add-envio/modal-add-envio";

// "dd/mm/yyyy, hh:mm:ss" (toLocaleString do Api) -> timestamp p/ filtro de data.
const ts = (s?: string): number => {
  const m = s?.match(/(\d{2})\/(\d{2})\/(\d{4}).*?(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return 0;
  return new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], +m[6]).getTime();
};
const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const dayEnd   = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).getTime();
const shorten  = (h?: string | null) => (!h ? "—" : h.length > 16 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h);
const isCompleted = (s?: string) => (s ?? "").toUpperCase() === "COMPLETED";

const Envios = () => {
  const dispatch = useAppDispatch();
  const list        = useAppSelector((s) => s.envios.list);
  const loading     = useAppSelector((s) => s.envios.loadingResults);
  const modalStatus = useAppSelector((s) => s.envios.modalAddStatus);
  const clientList  = useAppSelector((s) => s.user.clientList);
  const assetsList  = useAppSelector((s) => s.assets.assets);

  const [fClient, setFClient] = useState("");
  const [fAsset, setFAsset]   = useState("");
  const [fHash, setFHash]     = useState("");
  const [fStart, setFStart]   = useState<Date | null>(null);
  const [fEnd, setFEnd]       = useState<Date | null>(null);
  const [fMin, setFMin]       = useState("");
  const [fMax, setFMax]       = useState("");

  useEffect(() => {
    dispatch(fetchEnvios());
    if (!clientList || clientList.length === 0) dispatch(fetchClientsByOrganization());
    if (!assetsList || assetsList.length === 0) dispatch(fetchAssets());
    dispatch(fetchNetowrks());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    let r = list ?? [];
    if (fClient) r = r.filter((x) => String(x.client?.id) === fClient);
    if (fAsset)  r = r.filter((x) => String(x.asset?.id) === fAsset);
    if (fHash)   r = r.filter((x) => (x.hash ?? "").toLowerCase().includes(fHash.toLowerCase()));
    if (fStart)  r = r.filter((x) => ts(x.date) >= dayStart(fStart));
    if (fEnd)    r = r.filter((x) => ts(x.date) <= dayEnd(fEnd));
    if (fMin)    r = r.filter((x) => Number(x.value) >= Number(fMin));
    if (fMax)    r = r.filter((x) => Number(x.value) <= Number(fMax));
    return r;
  }, [list, fClient, fAsset, fHash, fStart, fEnd, fMin, fMax]);

  const toggle = () => dispatch(setModalAddStatus(!modalStatus));
  const clear = () => { setFClient(""); setFAsset(""); setFHash(""); setFStart(null); setFEnd(null); setFMin(""); setFMax(""); };

  const columns: DataListColumn<IEnvio>[] = [
    { key: "client",  header: "Cliente", width: "1.1fr", render: (r) => <strong>{r.client?.name ?? "—"}</strong> },
    {
      key: "hash", header: "Carteira", width: "1.4fr",
      render: (r) => (r.hash ? <span className="mono" title={r.hash}>{shorten(r.hash)}</span> : <span style={{ opacity: .5 }}>—</span>),
    },
    { key: "network", header: "Rede", render: (r) => r.network?.name ?? "—" },
    {
      key: "value", header: "Valor", align: "right", mono: true,
      render: (r) => <span>{formatAssetAmount(r.value, r.asset?.symbol)} <small style={{ opacity: .6 }}>{r.asset?.symbol}</small></span>,
    },
    { key: "asset",  header: "Asset",  render: (r) => r.asset?.symbol ?? "—" },
    { key: "date",   header: "Data",   mono: true, render: (r) => <span style={{ opacity: .55 }}>{r.date}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "done", header: "Concluído?", align: "center", width: "130px",
      render: (r) => isCompleted(r.status)
        ? <Check size={20} className="envio-done-check" aria-label="Concluído" />
        : <button type="button" className="btn-pill primary envio-done-btn" onClick={() => dispatch(confirmEnvio(r.id))}>Concluído</button>,
    },
  ];

  return (
    <>
      <div className="client-wrapper envios-wrapper">

        <div className="flex sb">
          <h1 className="page-title">
            Envios
            <small>envios da empresa para os clientes (debita a carteira da empresa e do cliente).</small>
          </h1>
          <div className="buttons">
            <button className="btn-pill primary" onClick={toggle}><Plus size={14} /> Adicionar Envio</button>
          </div>
        </div>

        {/* filtros */}
        <div className="envios-filters">
          <FormField label="Cliente" htmlFor="f-client">
            <select id="f-client" value={fClient} onChange={(e) => setFClient(e.target.value)}>
              <option value="">Todos</option>
              {clientList?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormField>
          <FormField label="Asset" htmlFor="f-asset">
            <select id="f-asset" value={fAsset} onChange={(e) => setFAsset(e.target.value)}>
              <option value="">Todos</option>
              {assetsList?.map((a: any) => <option key={a.id} value={a.id}>{a.symbol}</option>)}
            </select>
          </FormField>
          <FormField label="Hash" htmlFor="f-hash">
            <input id="f-hash" value={fHash} onChange={(e) => setFHash(e.target.value)} placeholder="carteira..." />
          </FormField>
          <FormField label="De" htmlFor="f-start">
            <DatePicker id="f-start" selected={fStart} onChange={(d: any) => setFStart(d)} dateFormat="dd/MM/yyyy" placeholderText="Início" isClearable showIcon />
          </FormField>
          <FormField label="Até" htmlFor="f-end">
            <DatePicker id="f-end" selected={fEnd} onChange={(d: any) => setFEnd(d)} dateFormat="dd/MM/yyyy" placeholderText="Fim" isClearable showIcon />
          </FormField>
          <FormField label="Valor mín" htmlFor="f-min" className="narrow">
            <input id="f-min" type="number" value={fMin} onChange={(e) => setFMin(e.target.value)} placeholder="0" />
          </FormField>
          <FormField label="Valor máx" htmlFor="f-max" className="narrow">
            <input id="f-max" type="number" value={fMax} onChange={(e) => setFMax(e.target.value)} placeholder="∞" />
          </FormField>
          <button className="btn-pill ghost" onClick={clear}>Limpar</button>
        </div>

        <DataList
          columns={columns}
          rows={rows}
          loading={!list || loading}
          rowKey={(r: IEnvio) => String(r.id)}
          empty="Sem envios registrados."
        />
      </div>

      {modalStatus && <ModalAddEnvio />}
    </>
  );
};

export default Envios;
